import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { download } from '../../utils/FileDownload';
import { DynamicTrimText } from '../common/TrimText';
import { formatDateRange, getSubjectStatus } from '../../utils/commonFunction';
import CommonCourseComponents from './CommonCourseComponents';
import ViewProfile from './ViewProfile';
import { Card } from '../common/GlobalElements/Card';
import { StandardButton } from '../common/GlobalElements/StandardButton';

const CourseCard = memo(({ course, type = 'qualification' }) => {
    const icon = getSubjectStatus(course?.icon);
    const progressValue = Number(course?.progress?.progressBar) || 0;
    const clampedProgressValue = Math.min(Math.max(progressValue, 0), 100);
    const wholeProgressValue = Math.trunc(clampedProgressValue);
    const hasTimetable = (course?.fileName && course?.file) || course?.timeTable;

    // Determine link path based on course type
    const getLinkPath = () => {
        if (type === 'shortCourse') {
            return `/shortCourse/${course?.id}`;
        }
        return `/mycourses/${course?.id}`;
    };

    // Render course code/title section (different for short courses vs qualifications)
    const renderHeader = () => {
        if (type === 'shortCourse') {
            return null;
        } else {
            return (
                <>
                    {course?.program_type_name ? (
                        <div className="couses-card">
                            <CommonCourseComponents degree={course?.program_type_name} />
                            {course?.commentCount !== 0 && (
                                <div className="nav-tpFixed-comments card-comment-box" title="You have unread messages">
                                    <span className="nav-fa-comments card-comments">
                                        <small className="notify-count">
                                            {course?.commentCount
                                                ? course?.commentCount < 10
                                                    ? course?.commentCount
                                                    : '9+'
                                                : 0}
                                        </small>
                                        <i className="fal fa-comments notification-icon"></i>
                                    </span>
                                </div>
                            )}
                        </div>
                    ) : null}
                </>
            );
        }
    };

    // Render title with comment count
    const renderTitle = () => {
        const showCommentIcon =
            type === 'shortCourse'
                ? !course?.code && course?.commentCount !== 0
                : !course?.program_type_name && course?.commentCount !== 0;

        return (
            <div className="program-tittle">
                <DynamicTrimText
                    className="text-primary text-left mb-0"
                    text={course?.course ? course?.course : 'NA'}
                    showTooltip={true}
                    trimText={false}
                />
                {showCommentIcon && (
                    <div className="nav-tpFixed-comments card-comment-box" title="You have unread messages">
                        <span className="nav-fa-comments card-comments">
                            <small className="notify-count">
                                {course?.commentCount ? (course?.commentCount < 10 ? course?.commentCount : '9+') : 0}
                            </small>
                            <i className="fal fa-comments notification-icon"></i>
                        </span>
                    </div>
                )}
            </div>
        );
    };

    // Render course type and duration
    const renderTypeAndDuration = () => {
        const typeDisplay = getTypeDisplay();
        const durationDisplay =
            course?.type === 'Self-Paced'
                ? course?.subject_duration
                    ? `${course.subject_duration} months`
                    : '-'
                : course?.start && course?.end
                  ? formatDateRange(course.start, course.end)
                  : '-';

        return (
            <>
                <p className="program-type" title={getTypeTitle()}>
                    {typeDisplay}
                </p>

                <div>
                    {course?.type === 'Self-Paced' ? (
                        <p className="course__card__duration" title={durationDisplay}>
                            <span className="couse_i_in">
                                <i className="fal fa-clock" style={{ marginRight: '5px' }} />
                            </span>
                            <span>{durationDisplay}</span>
                        </p>
                    ) : (
                        <p className="course__card__duration" title={durationDisplay}>
                            <span className="couse_i_in">
                                <i className="fal fa-calendar-days" style={{ marginRight: '5px' }} />
                            </span>
                            <span>{durationDisplay}</span>
                        </p>
                    )}
                </div>
            </>
        );
    };

    const renderShortCourseTitleAndDuration = () => {
        const showCommentIcon = !course?.code && course?.commentCount !== 0;
        const durationDisplay =
            course?.type === 'Self-Paced'
                ? course?.subject_duration
                    ? `${course.subject_duration} months`
                    : '-'
                : course?.start && course?.end
                  ? formatDateRange(course.start, course.end)
                  : '-';
        const typeDisplay = course?.type || '-';
        const durationIcon = course?.type === 'Self-Paced' ? 'fa-clock' : 'fa-calendar-days';

        return (
            <div className="short-course-title-duration course-card-content-stack">
                <div className="program-tittle short-course-title-wrap">
                    <DynamicTrimText
                        className="text-primary text-left mb-0"
                        text={course?.course ? course?.course : 'NA'}
                        showTooltip={true}
                        trimText={false}
                    />
                    {showCommentIcon && (
                        <div className="nav-tpFixed-comments card-comment-box" title="You have unread messages">
                            <span className="nav-fa-comments card-comments">
                                <small className="notify-count">
                                    {course?.commentCount
                                        ? course?.commentCount < 10
                                            ? course?.commentCount
                                            : '9+'
                                        : 0}
                                </small>
                                <i className="fal fa-comments notification-icon"></i>
                            </span>
                        </div>
                    )}
                </div>
                <div className="w-100 d-flex align-items-center short-course-type-duration gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <p className="program-type mb-0" title={typeDisplay}>
                            {typeDisplay}
                        </p>
                        <span className="text-border-color">|</span>
                        <p className="course__card__duration mb-0" title={durationDisplay}>
                            <span className="couse_i_in">
                                <i className={`fal ${durationIcon}`} style={{ marginRight: '5px' }} />
                            </span>
                            <span>{durationDisplay}</span>
                        </p>
                    </div>
                </div>
                <div className="qualification-view short-course-lecturer">{renderShortCourseProfile()}</div>
            </div>
        );
    };

    const getTypeTitle = () => {
        if (type === 'shortCourse') return course?.type || '';
        return course?.type && course?.number && course?.year
            ? `${course?.type}, Intake ${course?.number}, ${course?.year}`
            : '-';
    };

    const getTypeDisplay = () => {
        if (type === 'shortCourse') return course?.type || '';
        return course?.type && course?.number && course?.year
            ? `${course?.type}, Intake ${course?.number}, ${course?.year}`
            : '-';
    };

    // Render profile section
    const renderProfile = () => {
        return (
            <div className="w-100">
                <div className={`qualification-view ${type === 'qualification' ? 'reset-min-height' : ''}`}>
                    {type === 'shortCourse' ? renderShortCourseProfile() : renderQualificationProfile()}
                </div>
            </div>
        );
    };

    const renderShortCourseProfile = () => {
        if (course?.lecturer_name) {
            return (
                <ViewProfile
                    user={{
                        lead_photo: course?.lecturer_photo,
                        prog_lead_photo: course?.lecturer_photo,
                        prog_lead_name: course?.lecturer_name,
                        lead_activity_status: course?.lecturer_status,
                        lead_email: course?.lecturer_email,
                        lead_mobile: course?.lecturer_mobile,
                        coach_name: course?.intake_coach_name,
                        prog_coach_name: course?.intake_coach_name,
                        coach_photo: course?.intake_coach_photo,
                        coach_email: course?.intake_coach_email,
                        coach_mobile: course?.intake_coach_mobile,
                        supp_photo: course?.intake_supp_photo,
                        supp_fullname: course?.intake_supp_name,
                        supp_activity_status: course?.intake_supp_status,
                        supp_email: course?.intake_supp_email,
                        supp_mobile: course?.intake_supp_mobile,
                    }}
                    showStatus={false}
                    cardParent={true}
                />
            );
        } else if (course?.intake_coach_name) {
            return (
                <ViewProfile
                    user={{
                        lead_photo: course?.prog_coach_photo,
                        lead_name: course?.intake_coach_name,
                        prog_lead_name: course?.intake_coach_name,
                        lead_activity_status: course?.prog_lead_active === 1 ? 'online' : 'offline',
                        lead_email: course?.intake_coach_email,
                        lead_mobile: course?.intake_coach_mobile,
                        coach_photo: course?.intake_coach_photo,
                        supp_photo: course?.intake_supp_photo,
                        supp_fullname: course?.intake_supp_name,
                        supp_activity_status: course?.intake_supp_status,
                        supp_email: course?.intake_supp_email,
                        supp_mobile: course?.intake_supp_mobile,
                    }}
                    showStatus={false}
                    ele={course}
                    cardParent={false}
                    viewTimeTable={(course?.fileName && course?.file) || course?.timeTable ? true : false}
                />
            );
        } else if (course?.intake_supp_name) {
            return (
                <ViewProfile
                    user={{
                        lead_photo: course?.intake_supp_photo,
                        prog_lead_name: course?.intake_supp_name,
                        lead_activity_status: course?.intake_supp_status,
                        lead_email: course?.intake_supp_email,
                        lead_mobile: course?.intake_supp_mobile,
                    }}
                    showStatus={false}
                />
            );
        }
        return null;
    };

    const renderQualificationProfile = () => {
        return null; // Qualifications don't show profile in the original code
    };

    // Render progress section
    const renderProgress = () => {
        const progressClassName = `qualification-box-progress w-100${hasTimetable ? ' has-timetable-button' : ''}`;
        return (
            <div className={progressClassName}>
                {hasTimetable && (
                    <div className="quali_couses_ptg qualification-box-timetable">
                        <StandardButton
                            variant="white"
                            outline={true}
                            title="Download Timetable"
                            className={`download_btn ${type === 'qualification' ? 'text-nowrap' : ''}`}
                            onClick={(e) => {
                                e.preventDefault();
                                download(course?.file, course?.fileName);
                            }}
                        >
                            <i
                                className={`fal ${type === 'qualification' ? 'fa-calendar-alt' : 'fa-calendar-days'}`}
                            ></i>
                            {type === 'qualification' ? 'Timetable' : 'View Timetable'}
                        </StandardButton>
                    </div>
                )}
                <div className="progress progress-bar-wrapper">
                    <div
                        className="progress-bar zero-progress course-progress-bar"
                        role="progressbar"
                        style={{ width: `${clampedProgressValue}%` }}
                        aria-valuenow={wholeProgressValue}
                        aria-valuemin="0"
                        aria-valuemax="100"
                        title={`${wholeProgressValue}%`}
                    />
                </div>

                <div className="program-statistics-count">
                    <ul className="num-info">
                        {course?.progress?.total_videos > 0 && (
                            <li title="Videos" className="videoes">
                                <i className="fal fa-video" />
                                <span className="value">{course?.progress?.total_videos}</span>
                            </li>
                        )}
                        {course?.progress?.total_quizs > 0 && (
                            <li title="Quizzes" className="Quizzes">
                                <i className="fal fa-clipboard-list" />
                                <span className="value">{course?.progress?.total_quizs}</span>
                            </li>
                        )}
                        {course?.progress?.total_documents > 0 && (
                            <li title="Files" className="files">
                                <i className="fal fa-file" />
                                <span className="value">{course?.progress?.total_documents}</span>
                            </li>
                        )}
                        {course?.progress?.total_assessment > 0 && (
                            <li title="Assessment" className="files">
                                <i className="fal fa-ballot" />
                                <span className="value">{course?.progress?.total_assessment}</span>
                            </li>
                        )}
                    </ul>
                    <div className="d-flex align-items-center" style={{ margin: '0px 0' }}>
                        <p
                            className="status"
                            style={{ fontWeight: '600', fontSize: '10px', lineHeight: '10px', marginBottom: '0' }}
                        >
                            {wholeProgressValue}% Complete
                        </p>
                        {course?.subjectStatus ? (
                            <div className="d-flex align-items-center" style={{ paddingLeft: '16px' }}>
                                <img src={icon} alt="" width="10px" height="10px" />
                                <p className="course-status">{course?.subjectStatus}</p>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="card-wrapper" title={course?.course}>
            <Card
                as={Link}
                to={getLinkPath()}
                className={`course-card ${type === 'qualification' ? 'course-card--qualification' : 'course-card--short'}`}
                justifyContent="flex-start"
                alignItems="stretch"
                gap="var(--spacing-sm)"
                padding="var(--spacing-md)"
            >
                <div className="d-flex flex-column w-100 course-card-content-stack course-card-main-content">
                    {renderHeader()}
                    {type === 'qualification' ? renderTitle() : null}

                    {type === 'qualification' ? (
                        <>
                            <div className="qualification-card-type-row">
                                <div className="flex-1 qualification-type-duration-stack">
                                    {renderTypeAndDuration()}
                                </div>
                            </div>
                            <div className="qualification-card-profile-row">{renderProfile()}</div>
                        </>
                    ) : (
                        <>{renderShortCourseTitleAndDuration()}</>
                    )}
                </div>

                <div className="course-card-progress-anchor">{renderProgress()}</div>
            </Card>
        </div>
    );
});

export default CourseCard;
